import { logger } from "@platform/browser-logger";
import {
  flattenBenefitDefinitions,
  getBenefitDefinitions,
  type LocCoverageType,
} from "@platform/plan-grid-core";
import { buildCollapseAmountRows } from "@platform/formatting-utils";

import type { BenefitOrderIndex } from "./buildCommentDocument";

/**
 * Resolves one coverage type's benefit-table row order and labels, matching
 * what the SBS grid actually renders: `flattenBenefitDefinitions` gives the
 * definition-order rows (nested siblings re-parented after their parent),
 * then `buildCollapseAmountRows` — the same `transformRowData` the Quotes tab
 * passes to `PlanEditor` — drops collapsed/hidden rows and applies the grid's
 * row-label overrides.
 */
const buildOneCoverageOrderIndex = async (
  coverageType: LocCoverageType,
): Promise<BenefitOrderIndex> => {
  const definitions = await getBenefitDefinitions(coverageType);
  const flatRows = flattenBenefitDefinitions(definitions).map((row) => ({
    benefitKey: row.benefitKey,
    benefit: { label: row.label, nested: row.nested },
  }));

  const collapsedRows = buildCollapseAmountRows(coverageType)(flatRows) as {
    benefitKey: string;
    benefit: { label: string };
  }[];

  return {
    order: new Map(collapsedRows.map((row, index) => [row.benefitKey, index])),
    label: new Map(
      collapsedRows.map((row) => [row.benefitKey, row.benefit.label]),
    ),
  };
};

/**
 * Resolves the benefit order/label index for every distinct coverage type
 * given, keyed by the raw stored LOC coverage type string
 * (e.g. "dental-ppo"). Coverage types this drawer has no LOC definitions for
 * (none currently, but new LOCs land here first) are simply omitted — the
 * caller falls back to the raw benefit key.
 */
export const buildBenefitOrderIndexes = async (
  coverageTypes: ReadonlySet<string>,
): Promise<Map<string, BenefitOrderIndex>> => {
  const entries = await Promise.all(
    [...coverageTypes].map(async (coverageType) => {
      try {
        const index = await buildOneCoverageOrderIndex(
          coverageType as LocCoverageType,
        );
        return [coverageType, index] as const;
      } catch (error: unknown) {
        logger.error(
          "Needs Review Comments: no benefit definitions for coverage type",
          error instanceof Error ? error : undefined,
          { coverageType },
        );
        return undefined;
      }
    }),
  );

  return new Map(entries.filter((entry) => entry !== undefined));
};
